/**
 * The engagement chip. One component, so there is one chip.
 *
 * Six surfaces drew this by hand and no two agreed on the colour, the icon, the
 * weight or what an unscored contact should say — see lib/engagement.ts for the
 * full accounting. Anything showing a contact's Hot / Warm / Cool band renders
 * this; nothing else builds the chip itself.
 *
 * AN UNSCORED CONTACT RENDERS NOTHING. `getEngagementBand` returns null when
 * there is no score, and "no band" is a real answer: the table and global
 * search were passing `score || 0`, which labelled every unmeasured contact
 * Cool — a claim about someone nobody has looked at yet.
 *
 * THE CHIP CARRIES THE NUMBER — "Hot · 82" (Andrew, 2026-09-28). The band is a
 * bucket over a figure that decays daily, so 71 and 100 wore the same chip and
 * a salesperson had to open the record to learn which. The number is the
 * stored `engagementScore`, rounded, shown whether the score is computed or
 * pinned by hand. It is on by default, so every contact surface gets it; the
 * two places that already print the figure large beside the chip (the Summary
 * strip's Engagement cell and the modal's score card) pass
 * `showScore={false}` rather than say the same number twice.
 *
 * A chip given only a `band` — a legend, a stat header naming a band — has no
 * contact behind it and so no number; it reads "Hot" as before.
 */

import { Badge } from '@/components/ui/badge-base';
import { useTranslation } from '@/hooks/useTranslation';
import { ENGAGEMENT_CONFIG, engagementLabel } from '@/lib/engagement';
import { getEngagementBand, type EngagementBand } from '@shared/engagement';
import { cn } from '@/lib/utils';

interface EngagementBadgeProps {
  /** A contact's score. Renders nothing when there isn't one. */
  score?: number | null;
  /**
   * The band itself, for a legend or a stat header that names a band rather
   * than describing one contact — so those surfaces get the same chip instead
   * of a dot, a bare word, or an eyebrow with a muted icon beside it.
   */
  band?: EngagementBand;
  /**
   * Append the score after the band — "Hot · 82". On by default; pass `false`
   * only where the same figure is already printed beside the chip.
   */
  showScore?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function EngagementBadge({
  score,
  band,
  showScore = true,
  className,
  'data-testid': testId,
}: EngagementBadgeProps) {
  const t = useTranslation();
  const resolved = band ?? getEngagementBand(score);
  const config = resolved ? ENGAGEMENT_CONFIG[resolved] : null;
  if (!config) return null;

  const Icon = config.icon;
  const label = engagementLabel(resolved, t);
  const figure =
    showScore && typeof score === 'number' && Number.isFinite(score) ? Math.round(score) : null;

  return (
    <Badge
      variant="outline"
      preserveCase
      /* `normal-case tracking-normal` because this chip is dropped into label
         slots that are `type-eyebrow` — uppercase with wide tracking, which
         children inherit. The chip must read the same in a stat header as it
         does on a contact card.

         No `text-caption` here: Badge already sets it. Repeating it was not
         harmless — tailwind-merge treats a font-size token as overriding
         `leading-none`, so this chip took caption's 16px line height and stood
         27px tall beside every other 22px chip in the app. That was the "Hot
         is a different size from Registered" on the contact record. */
      className={cn(
        'flex w-fit shrink-0 items-center gap-1 normal-case tracking-normal',
        config.chip,
        className
      )}
      data-testid={testId}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {/* One inline run, so the spaces around the dot are real text rather
          than the flex gap — textContent reads "Hot · 82" exactly. */}
      <span>
        {label}
        {figure !== null && (
          <>
            <span aria-hidden="true" className="opacity-60">{' · '}</span>
            <span className="tabular-nums" data-engagement-score>{figure}</span>
          </>
        )}
      </span>
    </Badge>
  );
}
